import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Form } from 'foundations-components/transitional/components/Form';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'hs-lodash/debounce';
import { getBuyerOverridesPartialPaymentAmount } from '../../state/BuyerOverrides/selectors';
import { useCallback, useMemo } from 'react';
import BuyerOverridesActionTypes from '../../state/BuyerOverrides/actionTypes';
import { CurrencyInput } from 'foundations-components/transitional/components/CurrencyInput';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { useValidationErrors } from '../../hooks/useValidationErrors';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
// @ts-ignore Untyped module
import getNativeCurrencySymbol from 'I18n/utils/getNativeCurrencySymbol';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Text } from 'foundations-components/components/Text';
import { AMOUNT_REMAINING_LESS_THAN_MIN } from '../../constants/PaymentAmount';
import getPaymentAmountValidationError from './getPaymentAmountValidationError';
import { useSubtotals } from '../../context/SubtotalsProvider';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEBOUNCE_DELAY = 200;
const PaymentAmount = () => {
  var _checkoutSession$part;
  const dispatch = useDispatch();
  const {
    checkoutSession
  } = usePaymentFormContext();
  const {
    subtotalsValidationErrors,
    flushDebouncedGetValidationErrors
  } = useValidationErrors();
  const {
    validationError,
    thresholdAmount,
    messageKey
  } = useMemo(() => {
    const validationErrorsMap = subtotalsValidationErrors.reduce((acc, error) => {
      acc[error.subCategory] = error;
      return acc;
    }, {});
    return getPaymentAmountValidationError(validationErrorsMap);
  }, [subtotalsValidationErrors]);
  const isPartialPaymentsEnabled = (_checkoutSession$part = checkoutSession.partialPaymentsInfo) === null || _checkoutSession$part === void 0 ? void 0 : _checkoutSession$part.isPartialPaymentsEnabled;
  const partialPaymentAmount = useSelector(getBuyerOverridesPartialPaymentAmount);
  const balanceDue = checkoutSession.partialPaymentsInfo && checkoutSession.partialPaymentsInfo.balanceDue;
  const currencyCode = checkoutSession.currencyCode;
  const {
    partialPaymentsSubtotals
  } = useSubtotals();
  const amountRemaining = partialPaymentsSubtotals ? partialPaymentsSubtotals.amountRemaining : undefined;
  const updatePartialPaymentAmount = useCallback(event => {
    const amount = event.target.value || 0;
    dispatch({
      type: BuyerOverridesActionTypes.SET_PARTIAL_PAYMENT_AMOUNT,
      data: {
        partialPaymentAmount: amount
      }
    });
  }, [dispatch]);
  const onPartialPaymentAmountChange = debounce(updatePartialPaymentAmount, DEBOUNCE_DELAY);
  const handleBlur = useCallback(() => {
    onPartialPaymentAmountChange.flush();
    flushDebouncedGetValidationErrors();
  }, [flushDebouncedGetValidationErrors, onPartialPaymentAmountChange]);
  const isError = validationError !== null;
  const errorMessage = useMemo(() => {
    return isError && messageKey && thresholdAmount ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: messageKey,
      options: {
        currencyCode,
        value: thresholdAmount
      },
      elements: {
        Currency: FormattedIntlCurrency
      }
    }) : null;
  }, [currencyCode, messageKey, thresholdAmount, isError]);
  if (!isPartialPaymentsEnabled) {
    return null;
  }
  const showAmountRemaining = !validationError || validationError === AMOUNT_REMAINING_LESS_THAN_MIN;
  return /*#__PURE__*/_jsxs(View, {
    marginBlockEnd: "400",
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h4",
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.partialPayments.header"
      })
    }), /*#__PURE__*/_jsx(Form, {
      children: /*#__PURE__*/_jsx(FormControl, {
        required: true,
        error: isError,
        validationMessage: isError && errorMessage,
        children: /*#__PURE__*/_jsx(CurrencyInput, {
          max: balanceDue || undefined,
          "data-test-id": "payment-amount-input",
          currency: currencyCode,
          value: partialPaymentAmount,
          placeholder: I18n.text('payments.openAmount', {
            currencySymbol: getNativeCurrencySymbol(currencyCode)
          }),
          onChange: onPartialPaymentAmountChange,
          onBlur: handleBlur,
          onMouseLeave: handleBlur,
          use: "on-dark",
          styled: true
        })
      })
    }), showAmountRemaining && amountRemaining !== undefined && /*#__PURE__*/_jsx(View, {
      children: /*#__PURE__*/_jsxs(Text, {
        as: "small",
        color: validationError === AMOUNT_REMAINING_LESS_THAN_MIN ? 'text-alert-default' : 'text-core-subtle',
        "data-test-id": "amount-remaining",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.partialPayments.amountRemaining"
        }), ' ', /*#__PURE__*/_jsx(FormattedIntlCurrency, {
          currencyCode: currencyCode,
          value: amountRemaining
        })]
      })
    })]
  });
};
export default PaymentAmount;